import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, CircularProgress, TextField, Box, Typography, InputAdornment } from '@mui/material';
import Search from '@mui/icons-material/Search';

import useDebouncer from 'src/common/useDebouncer';
import { useAppDispatch } from 'src/redux-app-hooks';
import { showSnackbar, NotificationSeverity } from 'src/features/notifications/notifications.slice';
import { Contact } from '../crm.dto';
import { admiContactService } from '../admi-contact.service';

export function ContactAutocomplete({
    value,
    onSelectContact,
}: {
    value?: Contact | null;
    onSelectContact: (contact: Contact) => void;
}) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const debouncer = useDebouncer();

    function handleInputChange(_: unknown, value: string | null, reason: string) {
        // Only search when user types, not when selecting an option
        if (reason !== 'input' || !value) {
            return;
        }

        setLoading(true);

        debouncer(async () => {
            try {
                const contacts = await admiContactService.searchContacts(value);
                setContacts(contacts);
            } catch (error) {
                console.error('Failed to search contacts:', error);
                dispatch(
                    showSnackbar({
                        message: t('Failed to search contacts. Please try again.'),
                        severity: NotificationSeverity.Error,
                    }),
                );
                setContacts([]);
            } finally {
                setLoading(false);
            }
        }, 1000);
    }

    function handleChange(_: unknown, value: Contact | null) {
        if (value) {
            onSelectContact(value);
        }
    }

    return (
        <Autocomplete
            options={value && !contacts.some((c) => c.id === value.id) ? [value, ...contacts] : contacts}
            value={value ?? null}
            loading={loading}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChange={handleInputChange}
            onChange={handleChange}
            filterOptions={(x) => x} // this is required for the options to update in async mode
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={t('Search for a contact')}
                    slotProps={{
                        ...params.slotProps,
                        input: {
                            ...params.slotProps.input,
                            startAdornment: (
                                <>
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                    {params.slotProps.input.startAdornment}
                                </>
                            ),
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.slotProps.input.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                    <li key={key || option.id} {...optionProps}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {option.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {option.streetAddress}, {option.zipCodeAndCity}
                            </Typography>
                        </Box>
                    </li>
                );
            }}
        />
    );
}
